// Wave 20.50 — Slicing a planned route by distance-along.
//
// IDENTICAL FILE on web and mobile (web/src/services/routeGeometry.ts,
// mobile/src/services/routeGeometry.ts). Display-side geometry only:
// the server says a tuck zone runs from 1 800 m to 3 200 m, and the map
// needs the polyline for exactly that stretch. Same job RouteMap
// already does when it slices peaks out of a track by time.

export type Coord = [number, number]; // [lng, lat]

export function haversineM(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Cumulative distance at each vertex, metres. */
export function cumulativeM(coords: Coord[]): number[] {
  const out = [0];
  for (let i = 1; i < coords.length; i++) {
    out.push(
      out[i - 1] + haversineM(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]),
    );
  }
  return out;
}

/** Interpolate the point at `atM` along the route. */
export function pointAtDistance(coords: Coord[], cum: number[], atM: number): Coord | null {
  if (coords.length === 0) return null;
  if (atM <= 0) return coords[0];
  const total = cum[cum.length - 1];
  if (atM >= total) return coords[coords.length - 1];
  for (let i = 1; i < coords.length; i++) {
    if (cum[i] >= atM) {
      const span = cum[i] - cum[i - 1];
      const t = span > 0 ? (atM - cum[i - 1]) / span : 0;
      const a = coords[i - 1];
      const b = coords[i];
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
  }
  return coords[coords.length - 1];
}

/** The sub-polyline between two distances, endpoints interpolated so a
 *  zone boundary lands where the server put it rather than snapping to
 *  the nearest vertex. */
export function sliceByDistance(
  coords: Coord[],
  cum: number[],
  startM: number,
  endM: number,
): Coord[] {
  if (coords.length < 2 || !(endM > startM)) return [];
  const out: Coord[] = [];
  const first = pointAtDistance(coords, cum, startM);
  if (first) out.push(first);
  for (let i = 0; i < coords.length; i++) {
    if (cum[i] > startM && cum[i] < endM) out.push(coords[i]);
  }
  const last = pointAtDistance(coords, cum, endM);
  if (last) out.push(last);
  return out.length >= 2 ? out : [];
}

/** Distance from a point to the route, plus how far along the route the
 *  nearest point sits. Used on the ride to answer "where am I on this
 *  loop, and am I still on it?". Brute force over every vertex — a
 *  10 000-point loop is 10 000 haversines, under a millisecond, and it
 *  runs once per GPS fix (1 Hz). */
export function projectOntoRoute(
  coords: Coord[],
  cum: number[],
  lat: number,
  lng: number,
): { atM: number; offRouteM: number; index: number } {
  let bestD = Infinity;
  let bestI = 0;
  for (let i = 0; i < coords.length; i++) {
    const d = haversineM(lat, lng, coords[i][1], coords[i][0]);
    if (d < bestD) {
      bestD = d;
      bestI = i;
    }
  }
  return { atM: cum[bestI] ?? 0, offRouteM: bestD, index: bestI };
}
